import React, { useState, useEffect } from 'react';
import SearchIcon from '../../assets/images/Search1.svg';
import SearchResultItem from './SearchResultItem';
import {
    goTOPageHref,
    handlePageChange,
    highlightTextInIframe,
} from '../../utils/readiumUtils';
import { getProductSearchXml } from '../../helper';

interface SearchModalProps {
    onClose: () => void;
    bookUri: string;
    offset: number;
}

const SearchModal: React.FC<SearchModalProps> = ({
    onClose,
    bookUri,
    offset,
}) => {
    const [xhtmlContent, setXhtmlContent] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [results, setResults] = useState<
        {
            text: string;
            pageNumber: number;
            pageRef: string;
            bookMarkId: string;
        }[]
    >([]);

    useEffect(() => {
        const handleLoaded = (event: MessageEvent) => {
            if (event.data.type === 'iframeLoaded') {
                highlightTextInIframe(window, searchQuery);
                onClose();
            }
        };

        window.addEventListener('message', handleLoaded);
        return () => {
            window.removeEventListener('message', handleLoaded);
        };
    }, [searchQuery, onClose]);

    useEffect(() => {
        getProductSearchXml(bookUri)
            .then((res) => {
                if (res) setXhtmlContent(res);
            })
            .catch((err) => {
                console.log(err, ' error ');
            });
    }, [bookUri]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedQuery(searchQuery);
        }, 300);

        return () => {
            clearTimeout(handler);
        };
    }, [searchQuery]);

    useEffect(() => {
        if (!debouncedQuery) {
            setResults([]);
            return;
        }
        if (searchQuery.length < 3) {
            return;
        }
        const regex =
            /<page>[\s\S]*?<spineIndex>(\d+)<\/spineIndex>[\s\S]*?<pageRef>(.*?)<\/pageRef>[\s\S]*?<bodyText>(.*?)<\/bodyText>[\s\S]*?<bookMarkId>(.*?)<\/bookMarkId>[\s\S]*?<\/page>/g;

        const newResults: {
            text: string;
            pageNumber: number;
            pageRef: string;
            bookMarkId: string;
        }[] = [];
        let match;

        while ((match = regex.exec(xhtmlContent)) !== null) {
            const [_, spineIndex, pageRef, bodyText, bookMarkId] = match;
            const lowerCaseBodyText = bodyText.toLowerCase();
            const query = debouncedQuery.toLowerCase();

            let lastIndex = 0;
            let occurrenceIndex;
            while (
                (occurrenceIndex = lowerCaseBodyText.indexOf(
                    query,
                    lastIndex
                )) !== -1
            ) {
                const surroundingText = bodyText.substring(
                    Math.max(0, occurrenceIndex - 20),
                    Math.min(
                        bodyText.length,
                        occurrenceIndex + query.length + 20
                    )
                );
                newResults.push({
                    text: surroundingText,
                    pageNumber: parseInt(spineIndex, 10),
                    pageRef,
                    bookMarkId,
                });
                lastIndex = occurrenceIndex + query.length;
            }
        }
        const filtered_res = newResults.filter(
            (result) => result.pageNumber - offset > 0
        );
        setResults(filtered_res);
    }, [debouncedQuery]);

    return (
        <div
            className="fixed inset-0 z-50 flex justify-center bg-black bg-opacity-50"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-2xl p-6 w-3/4 h-fit my-10"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="mt-4 h-[3.5rem] flex items-center border-[0.2rem] rounded-lg px-4 py-2">
                    <input
                        type="text"
                        placeholder="Enter your keywords..."
                        className="flex-grow outline-none"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        id="searchBar"
                    />
                    <img
                        src={SearchIcon}
                        alt="Search"
                        className="h-6 w-6 ml-2"
                    />
                </div>
                <div className="mt-6 max-h-96 overflow-y-auto">
                    {results && results.length > 0 ? (
                        results.map((result, index) => (
                            <SearchResultItem
                                key={index}
                                text={result.text}
                                pageNumber={result.pageNumber}
                                pageRef={result.pageRef}
                                bookMarkId={result.bookMarkId}
                                offset={offset}
                                onClick={() => {
                                    goTOPageHref(window, result.pageRef);
                                    handlePageChange(window);
                                }}
                                searchWord={searchQuery}
                            />
                        ))
                    ) : (
                        <p className="text-gray-500 text-center">
                            Type atleast 3 words to search
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SearchModal;
